import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { requestEventAdvertisementChanges } from '@/api/sponsorApi'
import { handleAxiosError } from '@/utils/helper'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { X } from 'lucide-react'
import { showSuccessToast } from '@/components/ui/toast'
import CustomRichTextEditor from '../CustomRichTextEditor'
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_SUBJECT_LENGTH,
  REQUEST_MODIFICATION_FIELDS,
} from '@/constants/sponsor'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'

interface RequestModificationDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  advertisementId?: string
}

export function RequestModificationDrawer({
  open,
  onOpenChange,
  advertisementId,
}: Readonly<RequestModificationDrawerProps>) {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
  })
  const queryClient = useQueryClient()

  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleResetForm = () => {
    setFormData({
      subject: '',
      description: '',
    })
    setErrors({})
  }

  useEffect(() => {
    handleResetForm()
  }, [open])

  const getPlainText = (html: string) => {
    if (!html) return ''
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.body.textContent || '').replaceAll('\u00a0', ' ').trim()
  }

  const handleInputChange = (field: string, value: string) => {
    if (
      field === REQUEST_MODIFICATION_FIELDS.SUBJECT &&
      value.length > MAX_SUBJECT_LENGTH
    ) {
      setErrors(prev => ({
        ...prev,
        subject: `Subject must not exceed ${MAX_SUBJECT_LENGTH} characters`,
      }))
      return
    }

    if (field === REQUEST_MODIFICATION_FIELDS.DESCRIPTION) {
      const plainText = getPlainText(value)
      if (plainText.length > MAX_DESCRIPTION_LENGTH) {
        setErrors(prev => ({
          ...prev,
          description: `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`,
        }))
        return
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }))
    const isEmpty =
      field === 'description' ? !getPlainText(value) : !value.trim()
    if (isEmpty) {
      setErrors(prev => ({
        ...prev,
        [field]: `${field === 'subject' ? 'Subject' : 'Description'} is required`,
      }))
    } else if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const { mutate: requestModificationMutation, isPending } = useMutation({
    mutationFn: requestEventAdvertisementChanges,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SPONSOR_QUERY_KEYS.advertisementList],
      })
      showSuccessToast(
        'Modification request submitted successfully',
        'The sponsor will be notified and will respond within 24-48 hours.'
      )
      handleResetForm()
      onOpenChange(false)
    },
    onError: err => {
      handleAxiosError('Failed to submit modification request', err)
    },
  })

  const handleSubmit = () => {
    if (!advertisementId) return
    requestModificationMutation({
      id: advertisementId,
      payload: {
        subject: formData.subject,
        description: formData.description.trim(),
      },
    })
  }

  const handleCancel = () => {
    handleResetForm()
    onOpenChange(false)
  }

  // Check if form has required values
  const hasRequiredValues =
    !!formData.subject.trim() && !!getPlainText(formData.description)

  const hasErrors = Object.values(errors).some(error => error !== '')

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction="right"
      aria-label="Request modification drawer"
    >
      <DrawerContent className="h-full p-0 w-full max-w-full sm:max-w-[500px] rounded-none lg:rounded-xl">
        {/* Header */}
        <DrawerHeader className="border-b border-ss-neutral-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <DrawerTitle
              id="ad-details-title"
              className="text-ss-text-primary text-lg font-semibold leading-7"
            >
              Request Modification
            </DrawerTitle>
            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close advertisement details drawer"
                className="h-5 w-5"
              >
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Subject */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label
                variant="required"
                htmlFor="subject"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Subject
              </Label>
              <span className="text-xs text-ss-neutral-500">
                {formData.subject.length}/{MAX_SUBJECT_LENGTH}
              </span>
            </div>
            <Input
              id="subject"
              type="text"
              placeholder="Brief description of the modification needed"
              value={formData.subject}
              onChange={e => handleInputChange('subject', e.target.value)}
              className={errors.subject ? 'border-ss-critical-200' : ''}
              aria-describedby={errors.subject ? 'subject-error' : undefined}
              maxLength={MAX_SUBJECT_LENGTH}
            />
            {errors.subject && (
              <div
                id="subject-error"
                className="flex items-center gap-1 text-sm text-ss-critical-200"
                role="alert"
              >
                {errors.subject}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label
                variant="required"
                htmlFor="description"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Description
              </Label>
              <span className="text-xs text-ss-neutral-500">
                {getPlainText(formData.description).length}/
                {MAX_DESCRIPTION_LENGTH}
              </span>
            </div>
            <div className="relative">
              <CustomRichTextEditor
                value={formData.description}
                onChange={value => handleInputChange('description', value)}
                placeholder="Describe the specific changes you need, including details, context, and any relevant deadlines or requirements."
                className={errors.description ? 'border-ss-critical-200' : ''}
              />
              {errors.description && (
                <p className="text-sm text-ss-critical-200 mt-1">
                  {errors.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <DrawerFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            aria-label="Cancel modification request"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !hasRequiredValues || !advertisementId || isPending || hasErrors
            }
            className={
              !hasRequiredValues || !advertisementId || isPending || hasErrors
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }
            aria-label="Submit modification request"
          >
            {isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default RequestModificationDrawer
